import { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import InteractionHints from './InteractionHints';
import { applyFabricTexture, updateTextureZoom, remapExtrudeUVs } from './fabricTexture';
import FabricLoadingOverlay from './FabricLoadingOverlay';

const _isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
const DEFAULT_CAM = _isMobile ? { x: 0, y: 0.93, z: 1.11 } : { x: 0, y: 1.02, z: 1.22 };
const SCALE = 0.008;

function createRoundBackGeo(sA, sB, sC, sD) {
  const halfA = sA / 2;
  const halfB = sB / 2;
  const r = Math.max(sB - sC, 0.001); // corner radius = fullDepth - straightDepth

  const shape = new THREE.Shape();
  shape.moveTo(-halfA, -halfB);
  shape.lineTo(halfA, -halfB);
  shape.lineTo(halfA, sC - halfB);
  // Right back corner arc
  shape.absarc(halfA - r, sC - halfB, r, 0, Math.PI / 2, false);
  // Back straight edge
  shape.lineTo(-(halfA - r), halfB);
  // Left back corner arc
  shape.absarc(-(halfA - r), sC - halfB, r, Math.PI / 2, Math.PI, false);
  shape.lineTo(-halfA, -halfB);

  const bevel = Math.min(0.006, sD * 0.12);
  return new THREE.ExtrudeGeometry(shape, {
    steps: 1, depth: sD, bevelEnabled: true,
    bevelThickness: bevel, bevelSize: bevel, bevelSegments: 3, curveSegments: 48
  });
}

const RoundBackSeat3DPreview = ({
  width = 50, fullDepth = 50, straightDepth = 40, thickness = 5, rawThickness,
  fabricColor, fabricImageUrl, fabricZoom = 3, showZip = true,
  zipPlacement = 'back', rawA = 0, rawB = 0, rawC = 0, unit = 'cm',
  activeField = null,
  pipingOption = 'none', pipingColor, pipingImageUrl
}) => {
  const containerRef = useRef(null);
  const [fabricLoading, setFabricLoading] = useState(false);
  const [pipingLoading, setPipingLoading] = useState(false);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const cushionRef = useRef(null);
  const dimLinesRef = useRef(null);
  const zipGroupRef = useRef(null);
  const animFrameRef = useRef(null);
  const initializedRef = useRef(false);
  const resetCameraRef = useRef(false);
  const containerWidthRef = useRef(600);
  const dimLabelsRef = useRef({ a: null, b: null, c: null });
  const currentDimsRef = useRef(null);
  const targetDimsRef = useRef(null);
  const targetScaleYRef = useRef(1);
  const activeFieldRef = useRef(activeField);
  const pipingTopRef = useRef(null);
  const pipingBottomRef = useRef(null);

  const sA = Math.max(width * SCALE, 0.1);
  const sB = Math.max(fullDepth * SCALE, 0.1);
  const sC = Math.max(straightDepth * SCALE, 0.05);
  const sD = Math.max(thickness * SCALE, 0.03);
  const color = fabricColor || '#5a8a5a';

  const fabricImageUrlRef = useRef(fabricImageUrl);
  const colorRef = useRef(color);
  const fabricZoomRef = useRef(fabricZoom);

  useEffect(() => { activeFieldRef.current = activeField; }, [activeField]);
  useEffect(() => { fabricImageUrlRef.current = fabricImageUrl; colorRef.current = color; }, [fabricImageUrl, color]);
  useEffect(() => { fabricZoomRef.current = fabricZoom; }, [fabricZoom]);

  useEffect(() => { targetDimsRef.current = { a: sA, b: sB, c: sC, d: sD }; }, [sA, sB, sC, sD]);

  useEffect(() => {
    if (!containerRef.current || initializedRef.current) return;
    initializedRef.current = true;
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    containerWidthRef.current = rect.width;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(35, rect.width / rect.height, 0.01, 100);
    camera.position.set(DEFAULT_CAM.x, DEFAULT_CAM.y, DEFAULT_CAM.z);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setSize(rect.width, rect.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = true;
    controls.screenSpacePanning = true;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 0.15;
    controls.maxDistance = 4;
    controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.PAN };
    const isMobileInit = rect.width < 500;
    controls.target.set(0, isMobileInit ? 0.04 : 0, 0);
    controls.addEventListener('start', () => { resetCameraRef.current = false; });
    renderer.domElement.__orbitControls = controls;
    renderer.domElement.__renderFrame = () => renderer.render(scene, camera);
    controlsRef.current = controls;

    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dirLight = new THREE.DirectionalLight(0xffffff, 1.2);
    dirLight.position.set(3, 5, 3);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.set(1024, 1024);
    scene.add(dirLight);
    scene.add(new THREE.DirectionalLight(0xffffff, 0.4).translateX(-2).translateY(3).translateZ(-1));

    const ground = new THREE.Mesh(new THREE.PlaneGeometry(5, 5), new THREE.ShadowMaterial({ opacity: 0.15 }));
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -sD / 2 - 0.002;
    ground.receiveShadow = true;
    scene.add(ground);

    const geo = createRoundBackGeo(sA, sB, sC, sD);
    geo.rotateX(-Math.PI / 2);
    geo.translate(0, sD / 2, 0);
    remapExtrudeUVs(geo);
    geo.computeVertexNormals();
    const mat = new THREE.MeshStandardMaterial({ color: new THREE.Color(color), roughness: 0.85, metalness: 0.02 });
    const cushion = new THREE.Mesh(geo, mat);
    cushion.castShadow = true;
    scene.add(cushion);
    cushionRef.current = cushion;
    currentDimsRef.current = { a: sA, b: sB, c: sC, d: sD };

    const zipGroup = new THREE.Group();
    scene.add(zipGroup);
    zipGroupRef.current = zipGroup;

    // Dimension lines
    const aMat = new THREE.MeshBasicMaterial({ color: 0xaaaaaa });
    const bMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const cMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const dimArrowGeo = new THREE.ConeGeometry(0.007, 0.015, 6);

    // A line: light grey, closely spaced dots, no arrows
    const aDashes = [];
    for (let i = 0; i < 28; i++) { const d = new THREE.Mesh(new THREE.BoxGeometry(0.005, 0.003, 0.003), aMat); scene.add(d); aDashes.push(d); }

    // B line: white, closely spaced dots, with arrows
    const bDashes = [];
    for (let i = 0; i < 28; i++) { const d = new THREE.Mesh(new THREE.BoxGeometry(0.003, 0.003, 0.005), bMat); scene.add(d); bDashes.push(d); }
    const bArrow1 = new THREE.Mesh(dimArrowGeo, bMat); scene.add(bArrow1);
    const bArrow2 = new THREE.Mesh(dimArrowGeo, bMat); scene.add(bArrow2);

    // C line: white, closely spaced dots, with arrows
    const cDashes = [];
    for (let i = 0; i < 20; i++) { const d = new THREE.Mesh(new THREE.BoxGeometry(0.003, 0.003, 0.005), cMat); scene.add(d); cDashes.push(d); }
    const cArrow1 = new THREE.Mesh(dimArrowGeo, cMat); scene.add(cArrow1);
    const cArrow2 = new THREE.Mesh(dimArrowGeo, cMat); scene.add(cArrow2);

    dimLinesRef.current = { aDashes, bDashes, bArrow1, bArrow2, cDashes, cArrow1, cArrow2, aMat, bMat, cMat };

    // Piping rings (top + bottom)
    const pipingMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.6, metalness: 0.05 });
    const pipingTop = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat);
    const pipingBottom = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat.clone());
    pipingTop.visible = false;
    pipingBottom.visible = false;
    scene.add(pipingTop);
    scene.add(pipingBottom);
    pipingTopRef.current = pipingTop;
    pipingBottomRef.current = pipingBottom;

    const targetCamPos = new THREE.Vector3();
    resetCameraRef.current = true;

    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      // Thumbnail mode: hide dimension indicators and zip
      if (typeof window !== 'undefined' && window.__THUMBNAIL_MODE__) {
        const __dl = dimLinesRef.current;
        if (__dl) Object.values(__dl).forEach(v => {
          if (Array.isArray(v)) v.forEach(m => { if (m && m.visible !== undefined) m.visible = false; });
          else if (v && v.visible !== undefined) v.visible = false;
        });

              if (zipGroupRef.current) zipGroupRef.current.visible = false;
}
      controls.update();
      const td = targetDimsRef.current;
      let cd = currentDimsRef.current;
      if (cd && td) {
        const lerpSpeed = 0.1;
        const changed = Math.abs(cd.a - td.a) > 0.0001 || Math.abs(cd.b - td.b) > 0.0001 || Math.abs(cd.c - td.c) > 0.0001 || Math.abs(cd.d - td.d) > 0.0001;
        if (changed) {
          cd.a += (td.a - cd.a) * lerpSpeed;
          cd.b += (td.b - cd.b) * lerpSpeed;
          cd.c += (td.c - cd.c) * lerpSpeed;
          cd.d += (td.d - cd.d) * lerpSpeed;
          cushion.geometry.dispose();
          const geo = createRoundBackGeo(cd.a, cd.b, cd.c, cd.d);
          geo.rotateX(-Math.PI / 2);
          geo.translate(0, cd.d / 2, 0);
          remapExtrudeUVs(geo);
          geo.computeVertexNormals();
          cushion.geometry = geo;
          applyFabricTexture(cushion, fabricImageUrlRef.current, colorRef.current, fabricZoomRef.current);
        }
        ground.position.y = -cd.d / 2 - 0.002;

        // Track piping Y with lerped thickness
        if (pipingTopRef.current && pipingTopRef.current.visible) {
          const pBev = Math.min(0.006, cd.d * 0.12);
          pipingTopRef.current.position.y = cd.d * 1.5 + pBev * Math.SQRT1_2;
          pipingBottomRef.current.position.y = cd.d * 0.5 - pBev * Math.SQRT1_2;
        }

        if (dimLinesRef.current) {
          const bevel = Math.min(0.006, cd.d * 0.12);
          const topY = cd.d * 1.5 + bevel + 0.004;
          const halfA = cd.a / 2;
          const halfB = cd.b / 2;
          const curveStartZ = halfB - cd.c;

          const { aDashes, bDashes, bArrow1, bArrow2, cDashes, cArrow1, cArrow2 } = dimLinesRef.current;

          // A line at curve start (no arrows)
          for (let i = 0; i < aDashes.length; i++) {
            const t = (i + 0.5) / aDashes.length;
            aDashes[i].position.set(-halfA + cd.a * t, topY, curveStartZ);
          }

          // B line: front to back at center (back straight edge at -halfB)
          for (let i = 0; i < bDashes.length; i++) {
            const t = (i + 0.5) / bDashes.length;
            bDashes[i].position.set(0, topY, halfB - cd.b * t);
          }
          bArrow1.position.set(0, topY, halfB);
          bArrow1.rotation.set(Math.PI / 2, 0, 0);
          bArrow2.position.set(0, topY, -halfB);
          bArrow2.rotation.set(-Math.PI / 2, 0, 0);

          // C line: front to just touching A width line
          const cX = -halfA + cd.a * 0.15;
          const cEndZ = curveStartZ + 0.008;
          const cLen = halfB - cEndZ;
          for (let i = 0; i < cDashes.length; i++) {
            const t = (i + 0.5) / cDashes.length;
            cDashes[i].position.set(cX, topY, halfB - cLen * t);
          }
          cArrow1.position.set(cX, topY, halfB);
          cArrow1.rotation.set(Math.PI / 2, 0, 0);
          cArrow2.position.set(cX, topY, cEndZ);
          cArrow2.rotation.set(-Math.PI / 2, 0, 0);
        }

        if (resetCameraRef.current) {
          const maxDim = Math.max(td.a, td.b);
          const isMobile = containerWidthRef.current < 500;
          const distMult = isMobile ? 1.45 : 1.15;
          const distBase = isMobile ? 0.25 : 0.25;
          const camScale = isMobile ? 1.3 : 1.3;
          const dist = maxDim * distMult + distBase;
          targetCamPos.set(
            DEFAULT_CAM.x / camScale * dist,
            DEFAULT_CAM.y / camScale * dist,
            DEFAULT_CAM.z / camScale * dist
          );
          camera.position.lerp(targetCamPos, 0.15);
          const targetY = isMobile ? cd.d / 4 + 0.04 : cd.d / 4;
          controls.target.set(0, targetY, 0);
          if (camera.position.distanceTo(targetCamPos) < 0.02) { resetCameraRef.current = false; controls.saveState(); }
        }

        const halfA = cd.a / 2;
        const halfB = cd.b / 2;
        // Highlight active field
        const af = activeFieldRef.current;
        const YELLOW = 0xffd700;
        const activeBg = 'rgba(255,215,0,0.92)';
        const defaultBg = 'rgba(255,255,255,0.85)';
        const activeText = '#1a1a1a';
        const defaultText = '#374151';

        if (dimLinesRef.current) {
          dimLinesRef.current.aMat.color.set(af === 'a' ? YELLOW : 0xaaaaaa);
          dimLinesRef.current.bMat.color.set(af === 'b' ? YELLOW : 0xffffff);
          dimLinesRef.current.cMat.color.set(af === 'c' ? YELLOW : 0xffffff);
        }

        if (dimLabelsRef.current.a) {
          const v = new THREE.Vector3(0, cd.d * 0.5, halfB + 0.03);
          v.project(camera);
          dimLabelsRef.current.a.style.left = `${(v.x * 0.5 + 0.5) * containerWidthRef.current}px`;
          dimLabelsRef.current.a.style.top = `${(-v.y * 0.5 + 0.5) * rect.height}px`;
          dimLabelsRef.current.a.style.opacity = '1';
          dimLabelsRef.current.a.style.backgroundColor = af === 'a' ? activeBg : defaultBg;
          dimLabelsRef.current.a.style.color = af === 'a' ? activeText : defaultText;
        }
        if (dimLabelsRef.current.b) {
          const bTopY = cd.d * 1.5 + Math.min(0.006, cd.d * 0.12) + 0.004;
          const v = new THREE.Vector3(0.03, bTopY, 0);
          v.project(camera);
          dimLabelsRef.current.b.style.left = `${(v.x * 0.5 + 0.5) * containerWidthRef.current}px`;
          dimLabelsRef.current.b.style.top = `${(-v.y * 0.5 + 0.5) * rect.height}px`;
          dimLabelsRef.current.b.style.opacity = '1';
          dimLabelsRef.current.b.style.backgroundColor = af === 'b' ? activeBg : defaultBg;
          dimLabelsRef.current.b.style.color = af === 'b' ? activeText : defaultText;
        }
        if (dimLabelsRef.current.c) {
          const cLineX = -halfA + cd.a * 0.15;
          const cMidZ = halfB - cd.c / 2;
          const cTopY = cd.d * 1.5 + Math.min(0.006, cd.d * 0.12) + 0.004;
          const v = new THREE.Vector3(cLineX - 0.03, cTopY, cMidZ);
          v.project(camera);
          dimLabelsRef.current.c.style.left = `${(v.x * 0.5 + 0.5) * containerWidthRef.current}px`;
          dimLabelsRef.current.c.style.top = `${(-v.y * 0.5 + 0.5) * rect.height}px`;
          dimLabelsRef.current.c.style.opacity = '1';
          dimLabelsRef.current.c.style.backgroundColor = af === 'c' ? activeBg : defaultBg;
          dimLabelsRef.current.c.style.color = af === 'c' ? activeText : defaultText;
        }
      }
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      const r = container.getBoundingClientRect();
      containerWidthRef.current = r.width;
      camera.aspect = r.width / r.height;
      camera.updateProjectionMatrix();
      renderer.setSize(r.width, r.height);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animFrameRef.current);
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      initializedRef.current = false;
    };
  }, []);

  // Target dims are updated via useEffect at top; animate loop handles lerping
  useEffect(() => {
    if (!cushionRef.current) return;
    targetDimsRef.current = { a: sA, b: sB, c: sC, d: sD };
    resetCameraRef.current = true;
  }, [sA, sB, sC, sD]);

  useEffect(() => {
    if (cushionRef.current) {
      const needsLoad = fabricImageUrl && (fabricImageUrl.includes('warwick.com') || fabricImageUrl.startsWith('/api/fabric-images'));
      if (needsLoad) setFabricLoading(true);
      applyFabricTexture(cushionRef.current, fabricImageUrl, color, fabricZoom, () => setFabricLoading(false));
    }
  }, [color, fabricImageUrl]);

  useEffect(() => {
    if (cushionRef.current) updateTextureZoom(cushionRef.current, fabricZoom);
  }, [fabricZoom]);

  // Update piping rings (top + bottom, D-shape perimeter)
  useEffect(() => {
    const pt = pipingTopRef.current;
    const pb = pipingBottomRef.current;
    if (!pt || !pb) return;

    const showPiping = pipingOption !== 'none';
    pt.visible = showPiping;
    pb.visible = showPiping;
    if (!showPiping) { setPipingLoading(false); return; }

    const pColor = pipingOption === 'same' ? (color || '#c8b89a') : (pipingColor || '#f5f0eb');
    const pImageUrl = pipingOption === 'same' ? fabricImageUrl : pipingImageUrl;
    const needsLoad = pImageUrl && (pImageUrl.includes('warwick.com') || pImageUrl.startsWith('/api/fabric-images'));
    if (needsLoad) setPipingLoading(true);
    let __remaining = 2;
    const __done = () => { __remaining -= 1; if (__remaining <= 0) setPipingLoading(false); };
    applyFabricTexture(pt, pImageUrl, pColor, fabricZoom, needsLoad ? __done : null);
    applyFabricTexture(pb, pImageUrl, pColor, fabricZoom, needsLoad ? __done : null);

    const halfA = sA / 2;
    const halfB = sB / 2;
    const bevel = Math.min(0.006, sD * 0.12);
    const tubeRadius = 0.003;
    const expansion = bevel * Math.SQRT1_2;
    const cornerR = Math.max(sB - sC, 0.001);
    const frontR = Math.min(bevel * 2, sA * 0.04, sB * 0.04);

    // Build piping shape with rounded front corners
    const ps = new THREE.Shape();
    ps.moveTo(-halfA + frontR, -halfB);
    ps.lineTo(halfA - frontR, -halfB);
    ps.quadraticCurveTo(halfA, -halfB, halfA, -halfB + frontR);
    ps.lineTo(halfA, sC - halfB);
    ps.absarc(halfA - cornerR, sC - halfB, cornerR, 0, Math.PI / 2, false);
    ps.lineTo(-(halfA - cornerR), halfB);
    ps.absarc(-(halfA - cornerR), sC - halfB, cornerR, Math.PI / 2, Math.PI, false);
    ps.lineTo(-halfA, -halfB + frontR);
    ps.quadraticCurveTo(-halfA, -halfB, -halfA + frontR, -halfB);

    const pts2D = ps.getPoints(128);
    if (pts2D.length > 1 && pts2D[0].distanceTo(pts2D[pts2D.length - 1]) < 0.0001) pts2D.pop();

    // Convert to XZ plane: shape X→world X, shape Y→world -Z
    const pts3D = pts2D.map(p => new THREE.Vector3(p.x, 0, -p.y));

    // Expand outward
    const n = pts3D.length;
    const expanded = pts3D.map((pt3, i) => {
      const prev = pts3D[(i - 1 + n) % n];
      const next2 = pts3D[(i + 1) % n];
      const dx = next2.x - prev.x;
      const dz = next2.z - prev.z;
      const len = Math.sqrt(dx * dx + dz * dz) || 1;
      const nx = -dz / len;
      const nz = dx / len;
      const dot = nx * pt3.x + nz * pt3.z;
      const sign = dot >= 0 ? 1 : -1;
      return new THREE.Vector3(pt3.x + sign * nx * expansion, 0, pt3.z + sign * nz * expansion);
    });

    class LinearClosedCurve extends THREE.Curve {
      constructor(pts) { super(); this.pts = pts; }
      getPoint(t) {
        const n2 = this.pts.length;
        const f = t * n2;
        const i = Math.floor(f) % n2;
        const a = f - Math.floor(f);
        return new THREE.Vector3().lerpVectors(this.pts[i], this.pts[(i + 1) % n2], a);
      }
    }

    const curve = new LinearClosedCurve(expanded);
    const tubeGeo = new THREE.TubeGeometry(curve, 192, tubeRadius, 8, true);

    pt.geometry.dispose();
    pb.geometry.dispose();
    pt.geometry = tubeGeo;
    pb.geometry = tubeGeo.clone();

    pt.position.y = sD * 1.5 + expansion;
    pb.position.y = sD * 0.5 - expansion;
  }, [sA, sB, sC, sD, color, pipingOption, pipingColor, pipingImageUrl, fabricImageUrl, fabricZoom]);

  const handleReset = () => { resetCameraRef.current = true; };

  // Build zip
  useEffect(() => {
    if (!zipGroupRef.current) return;
    zipGroupRef.current.visible = showZip;
    if (!showZip) return;
    let lastKey = '';
    const buildZip = () => {
      const cd = currentDimsRef.current;
      if (!cd) return;
      const key = [cd.a, cd.b, cd.c, cd.d, zipPlacement].join(',');
      if (key === lastKey) return;
      lastKey = key;

      const zg = zipGroupRef.current;
      while (zg.children.length) { const ch = zg.children[0]; if (ch.geometry) ch.geometry.dispose(); if (ch.material) ch.material.dispose(); zg.remove(ch); }

      const halfA = cd.a / 2;
      const halfB = cd.b / 2;
      const r = Math.max(cd.b - cd.c, 0.001);
      const isBack = zipPlacement === 'back';
      const ePts = [];

      if (isBack) {
        // Trace back edge: right corner arc + straight back + left corner arc (75% coverage)
        const arcCenter1X = halfA - r;
        const arcCenter1Y = cd.c - halfB;
        const arcCenter2X = -(halfA - r);
        const arcCenter2Y = cd.c - halfB;
        const N = 48;
        for (let i = 0; i <= 12; i++) {
          const a = (i / 12) * (Math.PI / 2);
          ePts.push([arcCenter1X + r * Math.cos(a), arcCenter1Y + r * Math.sin(a)]);
        }
        const backSteps = Math.max(2, N - 24);
        for (let i = 1; i < backSteps; i++) {
          const t = i / backSteps;
          ePts.push([(halfA - r) * (1 - t) + (-(halfA - r)) * t, halfB]);
        }
        for (let i = 0; i <= 12; i++) {
          const a = Math.PI / 2 + (i / 12) * (Math.PI / 2);
          ePts.push([arcCenter2X + r * Math.cos(a), arcCenter2Y + r * Math.sin(a)]);
        }
      } else {
        // Front edge: entire A width (same as chair cushion)
        const steps = Math.max(2, Math.round(cd.a / 0.006));
        for (let i = 0; i <= steps; i++) {
          ePts.push([-halfA + cd.a * (i / steps), -halfB]);
        }
      }

      if (ePts.length < 2) return;
      const deduped = [ePts[0]];
      for (let i = 1; i < ePts.length; i++) {
        const dx = ePts[i][0] - deduped[deduped.length - 1][0];
        const dy = ePts[i][1] - deduped[deduped.length - 1][1];
        if (Math.sqrt(dx * dx + dy * dy) > 0.0002) deduped.push(ePts[i]);
      }
      if (deduped.length < 2) return;

      const yCenter = cd.d;
      const p3 = deduped.map(([sx, sy]) => new THREE.Vector3(sx, yCenter, -sy));

      // Offset outward from cushion surface — compute all offsets FIRST from original positions
      const OFF = 0.008;
      const offsets = p3.map((_, i) => {
        const prev = p3[Math.max(0, i - 1)];
        const next = p3[Math.min(p3.length - 1, i + 1)];
        const tan = new THREE.Vector3().subVectors(next, prev).normalize();
        const n = new THREE.Vector3(-tan.z, 0, tan.x);
        if (n.dot(p3[i]) < 0) n.negate();
        return n.multiplyScalar(OFF);
      });
      for (let i = 0; i < p3.length; i++) {
        p3[i].add(offsets[i]);
      }

      const curve = new THREE.CatmullRomCurve3(p3, false, 'centripetal', 0.5);

      // Zip track (white tube)
      const trackGeo = new THREE.TubeGeometry(curve, Math.max(48, deduped.length), 0.0035, 6, false);
      zg.add(new THREE.Mesh(trackGeo, new THREE.MeshBasicMaterial({ color: 0xffffff })));

      // Zip teeth
      const toothMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
      const totalLen = curve.getLength();
      const spacing = 0.014;
      const nTeeth = Math.floor(totalLen / spacing);
      for (let i = 0; i < nTeeth; i++) {
        const frac = (i + 0.5) / nTeeth;
        const pos = curve.getPoint(frac);
        const tan = curve.getTangent(frac);
        const normal = new THREE.Vector3(-tan.z, 0, tan.x).normalize();
        const side = (i % 2 === 0) ? 1 : -1;
        const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.006, 0.005, 0.004), toothMat);
        tooth.position.copy(pos).addScaledVector(normal, side * 0.007);
        tooth.rotation.y = -Math.atan2(tan.z, tan.x);
        zg.add(tooth);
      }
    };
    buildZip();
    const interval = setInterval(buildZip, 200);
    return () => clearInterval(interval);
  }, [zipPlacement, showZip]);

  return (
    <div className="w-full h-[280px] sm:h-[480px] rounded-xl overflow-hidden relative" style={{ background: 'linear-gradient(135deg, #f5f0eb 0%, #e8e0d8 100%)' }} data-testid="round-back-seat-3d-preview">
      {fabricLoading && <FabricLoadingOverlay />}
      {!fabricLoading && pipingLoading && <FabricLoadingOverlay label="Loading piping..." />}
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      <div ref={el => dimLabelsRef.current.a = el} data-testid="dim-label-a" className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }}>{rawA > 0 ? `${unit === 'mm' ? rawA * 10 : rawA} ${unit}` : `A ${unit}`}</div>
      <div ref={el => dimLabelsRef.current.b = el} data-testid="dim-label-b" className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }}>{rawB > 0 ? `${unit === 'mm' ? rawB * 10 : rawB} ${unit}` : `B ${unit}`}</div>
      <div ref={el => dimLabelsRef.current.c = el} data-testid="dim-label-c" className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }}>{rawC > 0 ? `${unit === 'mm' ? rawC * 10 : rawC} ${unit}` : `C ${unit}`}</div>
      {showZip && (
        <div className="hidden sm:flex absolute top-3 left-3 items-center gap-2 bg-white/85 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-600 pointer-events-none" data-testid="zip-legend">
          <svg width="24" height="6" viewBox="0 0 24 6"><line x1="0" y1="3" x2="24" y2="3" stroke="#333" strokeWidth="2" strokeDasharray="3,2" /></svg>
          <span className="font-medium">Zip on {zipPlacement === 'back' ? 'back edge' : 'front edge'}</span>
        </div>
      )}
      <button onClick={handleReset} className="absolute top-3 right-3 w-8 h-8 bg-white/85 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-white transition-colors shadow-sm cursor-pointer" title="Reset view">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 1-15.36 6.36"/><path d="M3 12a9 9 0 0 1 15.36-6.36"/><polyline points="21 3 21 9 15 9"/><polyline points="3 21 3 15 9 15"/></svg>
      </button>
      <InteractionHints className="hidden sm:flex absolute bottom-3 left-1/2 -translate-x-1/2 items-center gap-3 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-500 pointer-events-none whitespace-nowrap" />
    </div>
  );
};

export default RoundBackSeat3DPreview;
